import React, { type FC, useEffect, useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { html } from 'lit/static-html.js';
import {
  DataTable,
  Grid,
  GridCell,
  PageSection,
  Pagination,
  Spacer,
  Stack,
  TextTitle,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';

import { StudioPageProps } from '../../hooks';
import { WithLoadingState } from '../../router';

import { PerfChart } from './perf-chart';
import { PerfDetailDrawer } from './perf-detail-drawer';
import {
  ALL_SCENARIOS,
  CHART_METRICS,
  type ChartMetricKey,
  type PerfMetricRow,
  type PerfMetricSettings,
  type Scenario,
} from './types';

const NS = 'pro-studio/performance';
const DEFAULT_TYPE = 'workflows-table-bench';
const FETCH_LIMIT = 1000;
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_RANGE_DAYS = 30;
const ALL_FILTER = '__all__' as const;
// Default chart scenario when filter is set to "All".
const DEFAULT_CHART_SCENARIO: Scenario = 'cold-render';
// Height of the global Pro Studio nav — used to offset the detail overlay.
// No design-system token for this yet; centralised here so swapping it later
// is a one-line change.
const GLOBAL_NAV_HEIGHT_PX = 64;

type ScenarioFilter = typeof ALL_FILTER | Scenario;

// All date math is anchored to UTC. Hasura's `timestamp` column type is
// timezone-naive but `now()` writes UTC, so a local-time filter starts the
// window at the wrong instant for users east of UTC (their "today" begins
// hours before Hasura's). See review note #6.
function isoDateUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgoUtc(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return isoDateUtc(d);
}

type Row = {
  id: string;
  createdAt: string;
  scenario: string;
  size: number | string;
  commit: string;
  branch: string;
  pr: string;
  heapMb: string;
  scriptingMs: string;
  meanFps: string;
  domRows: number | string;
};

function fmt(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

function toRow(raw: PerfMetricRow): Row {
  const s = raw.settings ?? ({} as PerfMetricSettings);
  const ci = s.__meta?.ci;
  const heap = s.after?.jsHeapUsedSize ?? s.metrics?.jsHeapUsedSize;
  return {
    id: raw.id,
    createdAt: new Date(raw.createdAt).toLocaleString(),
    scenario: s.scenario ?? raw.type,
    size: s.size ?? '—',
    commit: ci?.sha?.slice(0, 7) ?? s.commit ?? '—',
    branch: ci?.branch ?? '—',
    pr: ci?.prNumber != null ? `#${ci.prNumber}` : '—',
    heapMb: heap != null ? `${(heap / 1024 / 1024).toFixed(1)} MB` : '—',
    scriptingMs: fmt(s.cpu?.scriptingMs),
    meanFps: fmt(s.fps?.meanFps),
    domRows: s.domRows ?? '—',
  };
}

function makeColumns(
  onView: (id: string) => void,
  t: (key: string, opts?: Record<string, unknown>) => string
): ColumnDef<Row>[] {
  return [
    { accessorKey: 'createdAt', header: t(`${NS}:table.created`) },
    { accessorKey: 'scenario', header: t(`${NS}:table.scenario`) },
    { accessorKey: 'size', header: t(`${NS}:table.rows`) },
    { accessorKey: 'commit', header: t(`${NS}:table.commit`) },
    { accessorKey: 'branch', header: t(`${NS}:table.branch`) },
    { accessorKey: 'pr', header: t(`${NS}:table.pr`) },
    { accessorKey: 'heapMb', header: t(`${NS}:table.heap`) },
    { accessorKey: 'scriptingMs', header: t(`${NS}:table.scripting`) },
    { accessorKey: 'meanFps', header: t(`${NS}:table.fps`) },
    { accessorKey: 'domRows', header: t(`${NS}:table.domRows`) },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        html`<ps-button
          variant="ghost"
          size="small"
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            onView(row.original.id);
          }}
        >
          ${t(`${NS}:table.details`)}
        </ps-button>`,
      meta: { align: 'end' },
    },
  ];
}

const PerformancePage: FC<StudioPageProps> = () => {
  const [t] = useTranslation();
  const [rows, setRows] = useState<PerfMetricRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scenarioFilter, setScenarioFilter] =
    useState<ScenarioFilter>(ALL_FILTER);
  const [metricKey, setMetricKey] = useState<ChartMetricKey>('jsHeapUsedSize');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<string>(() =>
    daysAgoUtc(DEFAULT_RANGE_DAYS)
  );
  const [toDate, setToDate] = useState<string>(() => isoDateUtc(new Date()));
  // `ps-pagination` is 1-based.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    urqlGql
      .getProStudioPerformanceMetrics({
        limit: FETCH_LIMIT,
        type: DEFAULT_TYPE,
        // Hasura's `timestamp` (not timestamptz) takes an ISO-8601 string
        // without timezone. Inclusive on both ends: full days from 00:00:00
        // to 23:59:59.999.
        from: fromDate ? `${fromDate}T00:00:00` : undefined,
        to: toDate ? `${toDate}T23:59:59.999` : undefined,
      })
      .toPromise()
      .then((resp) => {
        if (cancelled) return;
        if (resp.error) {
          setError(resp.error.message);
          setRows([]);
          return;
        }
        const fetched =
          (resp.data
            ?.tenancy_pro_studio_performance_metrics as PerfMetricRow[]) ?? [];
        setRows(fetched);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate]);

  const filteredRows = useMemo(() => {
    if (scenarioFilter === ALL_FILTER) return rows;
    return rows.filter((r) => r.settings?.scenario === scenarioFilter);
  }, [rows, scenarioFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(currentPage, pageCount);
  // Only map the visible slice — `toRow` allocates a new object per row and
  // was previously running over the full filtered set on every render.
  const pagedRows = useMemo(
    () =>
      filteredRows
        .slice((safePage - 1) * pageSize, safePage * pageSize)
        .map(toRow),
    [filteredRows, safePage, pageSize]
  );

  // Reset to first page whenever the dataset that backs the table changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [scenarioFilter, fromDate, toDate]);

  const rowsById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const selectedRow = selectedId ? (rowsById.get(selectedId) ?? null) : null;
  const columns = useMemo(() => makeColumns(setSelectedId, t), [t]);
  // Chart always plots one scenario. When the table filter is "All", default
  // to cold-render so something is always visible.
  const chartScenario: Scenario =
    scenarioFilter === ALL_FILTER ? DEFAULT_CHART_SCENARIO : scenarioFilter;

  const scenarioLabel = (s: Scenario): string =>
    t(`${NS}:scenarios.${s}`, { defaultValue: s });
  const metricLabel =
    CHART_METRICS.find((m) => m.key === metricKey)?.label ?? metricKey;

  return (
    <React.Fragment>
      <PageSection test-id="ps-perf-chart-section">
        <Grid alignItems="center">
          <GridCell xsmall="12" large="6">
            <Stack alignItems="baseline" gutter="small">
              <TextTitle test-id="ps-perf-page-title" size="xsmall">
                {t(`${NS}:title`)}
              </TextTitle>
              <span
                style={{
                  color: 'var(--ps-color-text-subtle, #6b7280)',
                  fontSize: 13,
                }}
              >
                {t(`${NS}:subtitle`, {
                  type: DEFAULT_TYPE,
                  filtered: filteredRows.length,
                  total: rows.length,
                })}
              </span>
            </Stack>
          </GridCell>
          <GridCell xsmall="12" large="6">
            <Stack alignItems="center" justifyContent="end" gutter="small">
              <label
                style={{
                  fontSize: 13,
                  color: 'var(--ps-color-text, #374151)',
                }}
              >
                {t(`${NS}:filters.from`)}&nbsp;
                <input
                  type="date"
                  value={fromDate}
                  max={toDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ padding: '4px 8px' }}
                  data-testid="ps-perf-from-date"
                />
              </label>
              <label
                style={{
                  fontSize: 13,
                  color: 'var(--ps-color-text, #374151)',
                }}
              >
                {t(`${NS}:filters.to`)}&nbsp;
                <input
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ padding: '4px 8px' }}
                  data-testid="ps-perf-to-date"
                />
              </label>
              <label
                style={{
                  fontSize: 13,
                  color: 'var(--ps-color-text, #374151)',
                }}
              >
                {t(`${NS}:filters.scenario`)}&nbsp;
                <select
                  value={scenarioFilter}
                  onChange={(e) =>
                    setScenarioFilter(e.target.value as ScenarioFilter)
                  }
                  style={{ padding: '4px 8px' }}
                  data-testid="ps-perf-scenario-select"
                >
                  <option value={ALL_FILTER}>{t(`${NS}:filters.all`)}</option>
                  {ALL_SCENARIOS.map((s) => (
                    <option key={s} value={s}>
                      {scenarioLabel(s)}
                    </option>
                  ))}
                </select>
              </label>
            </Stack>
          </GridCell>
        </Grid>

        <Spacer />

        {error && (
          <>
            <div
              style={{
                background: 'var(--ps-color-bg-danger-subtle, #fef2f2)',
                color: 'var(--ps-color-text-danger, #991b1b)',
                padding: 12,
                borderRadius: 4,
                fontSize: 13,
              }}
            >
              {t(`${NS}:errors.loadFailed`, { message: error })}
            </div>
            <Spacer />
          </>
        )}

        <Stack
          alignItems="center"
          justifyContent="space-between"
          gutter="small"
        >
          <Stack alignItems="baseline" gutter="small">
            <span
              style={{
                color: 'var(--ps-color-text, #374151)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {scenarioLabel(chartScenario)}
            </span>
            <span
              style={{
                color: 'var(--ps-color-text-subtle, #6b7280)',
                fontSize: 12,
              }}
            >
              {t(`${NS}:chart.caption`, { metric: metricLabel })}
            </span>
          </Stack>
          <label
            style={{ fontSize: 13, color: 'var(--ps-color-text, #374151)' }}
          >
            {t(`${NS}:filters.metric`)}&nbsp;
            <select
              value={metricKey}
              onChange={(e) => setMetricKey(e.target.value as ChartMetricKey)}
              style={{ padding: '4px 8px' }}
              data-testid="ps-perf-metric-select"
            >
              {CHART_METRICS.map((m) => {
                const label = t(`${NS}:metrics.${m.key}`, {
                  defaultValue: m.label,
                });
                return (
                  <option key={m.key} value={m.key}>
                    {label}
                    {m.unit ? ` (${m.unit})` : ''}
                  </option>
                );
              })}
            </select>
          </label>
        </Stack>
        <PerfChart rows={rows} scenario={chartScenario} metricKey={metricKey} />
      </PageSection>

      <PageSection test-id="ps-perf-table-section">
        {/*
          font-size on the parent inherits across the shadow DOM boundary so
          cell text shrinks; `display: block; width: 100%` forces the
          ps-data-table host (which is `display: inline` by default — no
          :host rule in the WC) to fill the PageSection. Without these two
          tweaks the table collapses to its minimum content width and
          scrolls horizontally inside a narrow card.
        */}
        <DataTable
          test-id="ps-perf-runs-table"
          size="xsmall"
          columns={columns}
          data={pagedRows}
          actionable
          style={{
            display: 'block',
            width: '100%',
            fontSize: 12,
          }}
        />

        {filteredRows.length > 0 && (
          <div style={{ padding: '8px 4px 0' }} data-testid="ps-perf-pager">
            <Pagination
              total={filteredRows.length}
              pageSize={pageSize}
              currentPage={safePage}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onChange={({ detail }) => setCurrentPage(detail.page)}
              onShowSizeChange={({ detail }) => {
                setPageSize(detail.pageSize);
                setCurrentPage(1);
              }}
            />
          </div>
        )}

        {!loading && filteredRows.length === 0 && !error && (
          <div
            style={{
              padding: 24,
              color: 'var(--ps-color-text-subtle, #6b7280)',
              fontSize: 13,
              textAlign: 'center',
            }}
          >
            <div>{t(`${NS}:empty.title`)}</div>
            <div>{t(`${NS}:empty.hint`)}</div>
          </div>
        )}
      </PageSection>

      {/* Detail panel: fixed right-side overlay so it never squeezes the table. */}
      {selectedRow && (
        <>
          <div
            onClick={() => setSelectedId(null)}
            style={{
              position: 'fixed',
              top: GLOBAL_NAV_HEIGHT_PX,
              right: 0,
              bottom: 0,
              left: 0,
              background: 'var(--ps-color-overlay, rgba(15, 23, 42, 0.25))',
              zIndex: 40,
            }}
            aria-hidden
          />
          <div
            style={{
              position: 'fixed',
              top: GLOBAL_NAV_HEIGHT_PX,
              right: 0,
              bottom: 0,
              width: 'min(640px, 95vw)',
              zIndex: 50,
              boxShadow: '-4px 0 16px rgba(15, 23, 42, 0.15)',
              background: 'var(--ps-color-surface, #ffffff)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            <PerfDetailDrawer
              row={selectedRow}
              onClose={() => setSelectedId(null)}
            />
          </div>
        </>
      )}
    </React.Fragment>
  );
};

export const Performance = WithLoadingState(PerformancePage);
